import type { MusicalToken } from '../../types/music';
import { CHILEAN_FIGURE_LABEL } from '../tokens';
import { restTokenFromDurationBeats } from './restTokens';

const GRID: Array<{ beats: number; token: MusicalToken }> = [
  { beats: 4, token: 'W' },
  { beats: 2, token: 'H' },
  { beats: 1, token: 'Q' },
  { beats: 0.5, token: 'E' },
  { beats: 0.25, token: 'S' },
];

const PITCH_TO_LABEL: Record<string, string> = {
  c: 'Do',
  d: 'Re',
  e: 'Mi',
  f: 'Fa',
  g: 'Sol',
  a: 'La',
  b: 'Si',
};

/** Cuantiza duración en negras → token W/H/Q/E/S o silencio R_*. */
export function beatsToMusicalToken(beats: number, isRest = false, dotted = false): MusicalToken {
  if (isRest) return restTokenFromDurationBeats(beats, dotted);
  const b = Math.max(beats, 0.0625);
  const base = dotted ? b / 1.5 : b;
  let best: MusicalToken = 'Q';
  let bestDiff = Infinity;
  for (const row of GRID) {
    const diff = Math.abs(base - row.beats);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = row.token;
    }
  }
  return best;
}

export function pitchToSpanishLabel(pitch: string): string {
  const m = String(pitch || 'g/4').match(/^([a-g])([#b]?)/i);
  if (!m) return 'Do';
  const letter = m[1].toLowerCase();
  const acc = m[2] === '#' ? '♯' : m[2] === 'b' ? '♭' : '';
  return (PITCH_TO_LABEL[letter] || letter.toUpperCase()) + acc;
}

export function midiNumberToPitch(midi: number): string {
  const names = ['c', 'c', 'd', 'd', 'e', 'f', 'f', 'g', 'g', 'a', 'a', 'b'];
  const octave = Math.floor(midi / 12) - 1;
  const name = names[midi % 12] ?? 'c';
  const sharp = [1, 3, 6, 8, 10].includes(midi % 12);
  return `${name}${sharp ? '#' : ''}/${octave}`;
}

export function cleanFigureLabel(text: string): string {
  let t = String(text || '').trim();
  t = t.replace(/\([^)]*\d[^)]*\)/g, '');
  t = t.replace(/\([^)]*(tiempo|beat|medio)[^)]*\)/gi, '');
  t = t.replace(/^\W+|\W+$/g, '');
  const lower = t.toLowerCase();
  for (const [token, label] of Object.entries(CHILEAN_FIGURE_LABEL)) {
    if (lower.includes(label.toLowerCase())) return label;
  }
  if (/blanca/i.test(t)) return 'Blanca';
  if (/redonda/i.test(t)) return 'Redonda';
  if (/semicorchea|cuartina/i.test(t)) return 'Semicorchea';
  if (/corchea/i.test(t)) return 'Corchea';
  if (/negra/i.test(t)) return 'Negra';
  if (/silencio/i.test(t)) return 'Silencio de negra';
  return t.replace(/\([^)]+\)/g, '').trim() || t;
}

export function tokenFromLooseText(text: string): MusicalToken | null {
  const t = String(text || '').trim().toUpperCase();
  if (['W', 'H', 'Q', 'E', 'S', 'R_Q'].includes(t)) return t as MusicalToken;
  const lower = String(text).toLowerCase();
  if (/redonda/.test(lower)) return 'W';
  if (/blanca/.test(lower)) return 'H';
  if (/semicorchea|cuartina/.test(lower)) return 'S';
  if (/corchea/.test(lower)) return 'E';
  if (/silencio/.test(lower)) return 'R_Q';
  if (/negra/.test(lower)) return 'Q';
  return null;
}
